<script lang="ts" generics="OPTION extends string | number">
    import type {ButtonStyle, ComponentSize} from '../types';
    import {createEventDispatcher, onMount, tick} from 'svelte';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {
        Listbox,
        ListboxButton,
        ListboxOptions,
        ListboxOption,
    } from '@rgossiaux/svelte-headlessui';

    export let id: string = null;
    export let options: (OPTION)[] = [];
    export let value: OPTION;
    export let disabled = false;
    export let use = [];
    export let size: ComponentSize = 'md';
    export let additionalButtonClass = '';
    export let additionalContainerClass = '';
    export let buttonStyle: ButtonStyle = 'default';
    export let isOptionDisabled: (option: string | number) => boolean = () => false;

    const dispatch = createEventDispatcher<{ change: OPTION }>();
    const [floatingRef, floatingContent, update] = createFloatingActions(floatingUiCommonSettings('bottom-start'));

    let currentValue = value;
    let lastValue = value; // to distinguish between change from `value` prop (top-down) and change from `currentValue` prop binding (bottom-up)
    $: {
        if (value !== currentValue && value !== lastValue) {
            onValuePropertyChange();
        }
        if (currentValue !== value && currentValue !== lastValue) {
            onCurrentValueBindingChange();
        }
    }

    onMount(async () => {
        await tick();
        update();
    });

    function onValuePropertyChange() {
        currentValue = value;
        lastValue = value;
    }

    function onCurrentValueBindingChange() {
        value = currentValue;
        lastValue = currentValue;
        dispatch('change', currentValue);
    }
</script>

<Listbox {id}
         class="kp-dropdown-listbox {additionalContainerClass}"
         use={[floatingRef]}
         bind:value={currentValue}
         on:click={() => update()}
         {disabled}
         {...$$restProps}
         let:open>

    <slot name="label"/>

    <ListboxButton type="button"
                   role="combobox"
                   value={currentValue}
                   class="kp-dropdown-menu-toggle input-{size} btn btn-{buttonStyle} {open ? 'active': ''} {additionalButtonClass}"
                   {use}>
        <slot name="button"/>
    </ListboxButton>

    <ListboxOptions static
                    use={[floatingContent]}
                    class="kp-listbox-dropdown-menu size-{size} {open ? 'opened' : ''}">

        {#each options as option}
            <ListboxOption value={option}
                           disabled={disabled || isOptionDisabled(option)}
                           class="{({active, selected}) => `kp-listbox-dropdown-option ${active ? 'active' : ''} ${selected ? 'selected' : ''}`}">

                <slot name="option" {option}/>
            </ListboxOption>
        {/each}
    </ListboxOptions>
</Listbox>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";
    @import (reference) "styles/portaro.variables.less";

    :global {
        .kp-dropdown-listbox {
            .kp-dropdown-menu-toggle {
                display: flex;
                align-items: center;
            }

            .kp-listbox-dropdown-menu {
                display: flex;
                flex-direction: column;
                background-color: @body-bg;
                border: 1px solid @panel-default-border;
                height: auto;
                z-index: 100;
                max-height: 200px;
                min-width: 150px;
                overflow-x: hidden;
                opacity: 0;
                visibility: hidden;
                transform: translateY(5px);
                border-radius: @border-radius-default;
                box-shadow: rgba(66, 66, 66, 0.1) 0 2px 6px 0;
                transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out, transform 0.2s;

                &.size-lg {
                    font-size: @font-size-large;
                }

                &.size-md {
                    font-size: @font-size-base;
                }

                &.size-sm {
                    font-size: @font-size-sm;
                }

                &.size-xs {
                    font-size: @font-size-small;
                }

                &.opened {
                    opacity: 1;
                    visibility: visible;
                    transform: translateY(0);
                }

                .kp-listbox-dropdown-option {
                    padding: @spacing-s @spacing-m;
                    cursor: pointer;
                    text-wrap: nowrap;
                    transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;

                    &:disabled {
                        opacity: 0.5;
                        background-color: #F5F5F5;
                    }

                    &:not(:last-child) {
                        border-bottom: 1px solid @panel-default-border;
                    }

                    &.active {
                        background-color: #EAEEFF;
                    }

                    &.selected {
                        background-color: var(--accent-blue-new);
                        color: white;
                    }
                }
            }
        }
    }
</style>